import { g as getDateFromDatepicker } from './get-date-from-datepicker.js';
import { t as translateDate } from './translate-date.js';
import './index3.js';
import './index2.js';

/**
 * **Alaric**
 *
 * @exports bs_104
 * @category engines
 */
function factory({ window, document, thn }) {

  const engine = {};
  let priceRefresh;
  let bookingConfirmNights = 0;

  const isEngineLoadingData = () => {
    const loaders = document.querySelector('rect, #div-loader, #nuxt-loading, .btn-close-calendar');
    return thn.isVisible(loaders);
  };

  /**
     * @memberOf module:bs_104
     * @see {@link prestart}
     *
     * @param {object} ctx - The context object.
     */
  engine.boot = function boot(_ctx) {

    const container = document.querySelector('body');

    if (container) {

      const pageRefresh = thn.debounce(thn.pageCheck, 250);

      thn.observeDom(container, pageRefresh, {
        childList: true,
        subtree: true,
      });

      thn.shutdown(() => {
        pageRefresh.cancel();
        thn.stopObserveDom(container, pageRefresh);
      });
    }
  };

  /**
     * @memberOf module:bs_104
     * @see {@link onStateChange}
     *
     * @param {object} ctx - The context object.
     */
  engine.onStateChange = thn.debounce(() => {
    thn.pageCheck();
  }, 400);

  /**
     * @memberOf module:bs_104
     * @this thn.widgets.price
     * @see {@link Price#observe_dom}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceObserveDom = function priceObserveDom(ctx) {

    // Desktop || Mobile
    const container = document.querySelector('#__layout');

    if (container && !priceRefresh) {

      priceRefresh = thn.debounce(() => {
        if (!isEngineLoadingData()) {
          ctx.widget.refresh();
        }
      }, 500);

      thn.observeDom(container, priceRefresh, {
        childList: true,
        subtree: true,
      });

      thn.pageUnload(() => {
        priceRefresh.cancel();
        thn.stopObserveDom(container, priceRefresh);
        priceRefresh = null;
      });

    }

    const dataLayer = window.dataLayer;
    if (dataLayer) {
      priceRefresh = ({ message }) => {
        if (message.event === 'view_item_list') {
          thn.debounce(() => {
            ctx.widget.refresh();
          }, 500)();
        }
      };

      thn.bus.on('dataLayer:push', priceRefresh);

      thn.pageUnload(() => {
        priceRefresh.cancel();
        thn.bus.detach('dataLayer:push', priceRefresh);
      });
    }
  };

  /**
     * *Desktop and mobile main version*
     *
     * @memberOf module:bs_104
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v1 = function priceParseData(_ctx) {

    const widgetData = {};

    const getDatesFromUrl = () => {
      const dateBegin = thn.parseUrlParameter('checkin');
      const dateEnd = thn.parseUrlParameter('checkout');
      return (dateBegin && dateEnd) ? { dateBegin, dateEnd } : {};
    };

    const getDatesFromDom = () => {
      const datesEl = document.querySelector('.sticky-info-date span, [class^=Room_form-reservation] div:nth-child(1)')?.textContent?.trim();
      const dateParts = datesEl?.split('-');
      if (dateParts?.length === 2) {
        const dateBeginHasYear = !!dateParts[0].match(/\d{4}/);
        const dateBeginStr = dateBeginHasYear ? dateParts[0].trim() : dateParts[0].concat(dateParts[1].match(/\d{4}/)?.[0]);
        const dateBegin = translateDate(dateBeginStr, thn.data.language);
        const dateEnd = translateDate(dateParts[1].trim(), thn.data.language);
        return { dateBegin, dateEnd };
      }
      return {};
    };

    const getDatesFromDataLayer = () => {
      const dataLayer = window.dataLayer || [];
      const ecommerceEl = dataLayer && thn.dataLayerHelper.get('ecommerce');
      if (ecommerceEl && ecommerceEl.items) {
        const dateBegin = thn.objectGet(ecommerceEl.items, 'item_category2')?.replace(/-/g, '/');
        const dateEnd = thn.objectGet(ecommerceEl.items, 'item_category3')?.replace(/-/g, '/');
        return { dateBegin, dateEnd };
      }
      return {};
    };

    const dates = getDatesFromDom() ?? getDatesFromDataLayer() ?? getDatesFromUrl();

    if (!dates.dateBegin || !dates.dateEnd) {
      return null;
    }

    widgetData.dateBegin = dates.dateBegin;
    widgetData.dateEnd = dates.dateEnd;
    const nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
    bookingConfirmNights = nights;

    const adultsFromDom = document.querySelector('.fa-users, .fa-user')?.closest('li, span')?.textContent?.trim();
    const adultsFromUrl = thn.parseUrlParameter('adults');
    widgetData.adults = (adultsFromDom && thn.parseNumber(adultsFromDom)) ?? (adultsFromUrl && thn.parseNumber(adultsFromUrl)) ?? 2;
    widgetData.children = (thn.parseNumber('children') && thn.parseNumber(thn.parseUrlParameter('children'))) ?? 0;
    widgetData.guests = widgetData.adults + widgetData.children;
    widgetData.rooms = 1;

    const pricesElem = document.querySelectorAll('p[class^=Room_price], .price span');
    const prices = pricesElem && Array.from(pricesElem).filter(thn.isVisible).map((elem) => thn.parseNumber(elem.textContent));
    const bestPrice = prices && Math.min(...prices);
    const currencySel = document.querySelector('.currency-box, .undefined .me-2')?.textContent?.trim();
    const currency = (currencySel && thn.parseCurrency(currencySel)) ?? (pricesElem && thn.parseCurrency(pricesElem[0].textContent));

    const noAvailability = !!document.querySelector('.bundle-room, [class^=Room_bundle-room-content]');

    if (bestPrice && currency && nights && !noAvailability) {
      widgetData.currency = currency;
      widgetData.price = bestPrice / nights;

    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * *Desktop version*
     *
     * @memberOf module:bs_104
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v2 = function priceParseData(_ctx) {

    const widgetData = {};

    if (thn.data.media.name !== 'desktop') {
      return widgetData;
    }

    const dateBegin = getDateFromDatepicker('#checkin-small')?.replace(/-/g, '/');
    const dateEnd = getDateFromDatepicker('#checkout-small')?.replace(/-/g, '/');

    let nights;
    if (dateBegin && dateEnd) {
      widgetData.dateBegin = dateBegin;
      widgetData.dateEnd = dateEnd;
      nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
      bookingConfirmNights = nights;
    }

    widgetData.adults = document.querySelector('#form-rsv-adult')?.value;
    widgetData.children = document.querySelector('#form-rsv-children')?.value;
    widgetData.guests = widgetData.adults;
    widgetData.rooms = 1;

    const prices = [...document.querySelectorAll('.room-container .money')].filter(thn.isVisible).map((elem) => thn.parseNumber(elem.textContent));
    const bestPrice = prices.sort((curr, next) => curr - next)[0];
    const currency = document.querySelector('.current_curr')?.textContent;

    if (bestPrice && currency && nights) {
      widgetData.currency = currency;
      widgetData.price = bestPrice / nights;

    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * *Mobile version*
     *
     * @memberOf module:bs_104
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v3 = function priceParseData(_ctx) {

    const widgetData = {};

    if (thn.data.media.name === 'desktop') {
      return widgetData;
    }

    const [dateBeginRaw, dateEndRaw] = [...document.querySelectorAll('input[aria-label*="date"]')].map((elem) => elem.value);

    if (dateBeginRaw && dateEndRaw) {
      widgetData.dateBegin = dateBeginRaw.split('-').reverse().join('/');
      widgetData.dateEnd = dateEndRaw.split('-').reverse().join('/');
      widgetData.nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
      bookingConfirmNights = widgetData.nights;
    }

    //* Order of elements matter, the url param does not always update, is best to check the DOM first
    const adultsQty = document.querySelector('input[aria-label="Adult(s)"]')?.parentElement.textContent || thn.parseUrlParameter('adults');
    const childrenQty = document.querySelector('input[aria-label="Children"]')?.parentElement.textContent || thn.parseUrlParameter('children');

    widgetData.adults = thn.parseNumber(adultsQty) || 2;
    widgetData.children = thn.parseNumber(childrenQty) || 0;
    widgetData.guests = widgetData.adults + widgetData.children;
    widgetData.rooms = 1;

    const currency = document.querySelector('.currencySelected')?.firstChild?.textContent.trim();

    const bestPrice = [...document.querySelectorAll('.s-price h2')]
      .map((el) => thn.parseNumber(el.textContent))
      .sort((curr, next) => curr - next)[0];

    if (bestPrice && currency && widgetData.nights) {
      widgetData.price = bestPrice / widgetData.nights;
      widgetData.currency = currency;
    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_104
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(_ctx) {

    const widgetData = {};

    // TODO: No booking id is provided on the Booking Confirmed
    // const bookingId = document.querySelector('');
    // if (!bookingId) {
    //     widgetData.set = false;
    //     return widgetData;
    // }
    // widgetData.bookingId = bookingId;

    if (thn.data.media.name !== 'desktop') {
      // Dates
      // Date format sample: "Check-in: 11 Oct 2022 - Check-out: 13 Oct 20222"
      const rawDatesElement = document.querySelector('.ch-ch')?.textContent || '';
      const [dateBeginRaw, dateEndRaw] = rawDatesElement?.split(' - ').map((elem) => elem.slice(-11)).filter(Boolean) || [];

      if (dateBeginRaw && dateEndRaw) {
        widgetData.dateBegin = translateDate(dateBeginRaw, thn.data.language);
        widgetData.dateEnd = translateDate(dateEndRaw, thn.data.language);
      }

      // Price & Currency
      const detailsRaw = document.querySelector('.card-custom .green .layout div:last-of-type')?.textContent || '';
      const [currencyRaw, priceRaw] = detailsRaw.trim().split(' ').slice(-2).filter(Boolean);

      widgetData.currency = currencyRaw;
      widgetData.price = thn.parseNumber(priceRaw);

    } else {
      // Dates
      const rawDateBegin = document.querySelector('[data-label="DATE"] h5')?.textContent.trim();

      if (rawDateBegin) {
        widgetData.dateBegin = translateDate(rawDateBegin, thn.data.language);
        widgetData.dateEnd = thn.dateToString(thn.addDays(widgetData.dateBegin, bookingConfirmNights));
      }

      // Price & Currency
      widgetData.currency = thn.getCurrencyCode(document.querySelector('.grand .currid')?.textContent);
      widgetData.price = thn.parseNumber(document.querySelector('.grand .money')?.textContent);
    }

    return widgetData;
  };

  return engine;
}

export { factory };
